import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  AnyElement,
  Position,
  Size,
  TextElement,
  ImageElement,
  ShapeElement,
  TableElement,
  TableCellStyle,
} from "../../types/canvas.types";
import { CanvasService } from "../../services/canvas.service";
import { ImageUrlService } from "../../services/image-url.service";
import { Subscription } from "rxjs";

@Component({
  selector: "lib-canvas-element",
  template: `
    <div
      #elementContainer
      class="canvas-element"
      [class.element-selected]="isSelected && !isLinearShape()"
      [class.dragging]="isDragging"
      [style.left.px]="element.position.x"
      [style.top.px]="element.position.y"
      [style.width.px]="element.size.width"
      [style.height.px]="element.size.height"
      [style.z-index]="element.zIndex"
      [style.transform]="'rotate(' + (element.rotation || 0) + 'deg)'"
      [style.opacity]="element.opacity || 1"
      (mousedown)="startDrag($event)"
      (click)="onElementClick($event)"
    >
      <!-- Live size overlay during resize -->
      <div *ngIf="isResizing" class="resize-size-indicator">
        {{ element.size.width }} × {{ element.size.height }}
      </div>
      <!-- Text Element -->
      <button
        *ngIf="element.type === 'text' && isSelected"
        class="text-drag-handle"
        type="button"
        title="Drag text box"
        (mousedown)="onTextHandleMouseDown($event)"
      >
        <span class="handle-dot"></span>
        <span class="handle-dot"></span>
        <span class="handle-dot"></span>
      </button>
      <div
        *ngIf="element.type === 'text'"
        class="text-element"
        [style.font-size.px]="getTextElement().fontSize"
        [style.font-family]="getTextElement().fontFamily"
        [style.font-weight]="
          getTextElement().bold ? '700' : getTextElement().fontWeight || '400'
        "
        [style.color]="getTextElement().color"
        [style.text-align]="getTextElement().textAlign"
        [style.font-style]="getTextElement().italic ? 'italic' : 'normal'"
        [style.text-decoration]="
          getTextElement().underline ? 'underline' : 'none'
        "
        [class.text-bold]="getTextElement().bold"
        [class.text-italic]="getTextElement().italic"
        [class.text-underline]="getTextElement().underline"
        contenteditable="true"
        (blur)="updateTextContent($event)"
        (click)="onTextClick($event)"
        (keydown)="onTextKeydown($event)"
        (input)="onTextInput($event)"
        spellcheck="false"
      >
        {{ getTextElement().content }}
      </div>

      <!-- Image Element -->
      <img
        *ngIf="element.type === 'image'"
        class="image-element"
        [src]="getResolvedImageUrl()"
        [alt]="getImageElement().alt"
        draggable="false"
      />

      <!-- Shape Element -->
      <svg
        *ngIf="element.type === 'shape'"
        class="shape-element"
        [attr.width]="element.size.width"
        [attr.height]="element.size.height"
        [style.overflow]="isLinearShape() ? 'visible' : 'hidden'"
      >
        <rect
          *ngIf="getShapeElement().shapeType === 'rectangle'"
          [attr.width]="element.size.width"
          [attr.height]="element.size.height"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <circle
          *ngIf="getShapeElement().shapeType === 'circle'"
          [attr.cx]="element.size.width / 2"
          [attr.cy]="element.size.height / 2"
          [attr.r]="
            Math.min(element.size.width, element.size.height) / 2 -
            getShapeElement().strokeWidth
          "
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <ellipse
          *ngIf="getShapeElement().shapeType === 'ellipse'"
          [attr.cx]="element.size.width / 2"
          [attr.cy]="element.size.height / 2"
          [attr.rx]="element.size.width / 2 - getShapeElement().strokeWidth"
          [attr.ry]="element.size.height / 2 - getShapeElement().strokeWidth"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <polygon
          *ngIf="getShapeElement().shapeType === 'triangle'"
          [attr.points]="getTrianglePoints()"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <polygon
          *ngIf="getShapeElement().shapeType === 'diamond'"
          [attr.points]="getDiamondPoints()"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <polygon
          *ngIf="getShapeElement().shapeType === 'star'"
          [attr.points]="getStarPoints()"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <polygon
          *ngIf="getShapeElement().shapeType === 'hexagon'"
          [attr.points]="getHexagonPoints()"
          [attr.fill]="getShapeElement().fill"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
        />

        <line
          *ngIf="getShapeElement().shapeType === 'line'"
          [attr.x1]="getLinePointCoordinate('start', 'x')"
          [attr.y1]="getLinePointCoordinate('start', 'y')"
          [attr.x2]="getLinePointCoordinate('end', 'x')"
          [attr.y2]="getLinePointCoordinate('end', 'y')"
          [attr.stroke]="getShapeElement().stroke"
          [attr.stroke-width]="getShapeElement().strokeWidth"
          stroke-linecap="round"
        />

        <!-- Arrow Shape -->
        <g *ngIf="getShapeElement().shapeType === 'arrow'">
          <defs>
            <marker
              [attr.id]="'arrowhead-' + element.id"
              [attr.markerWidth]="getArrowheadDimensions().width"
              [attr.markerHeight]="getArrowheadDimensions().height"
              [attr.refX]="getArrowheadDimensions().refX"
              [attr.refY]="getArrowheadDimensions().refY"
              orient="auto"
              markerUnits="strokeWidth"
              overflow="visible"
            >
              <polygon
                [attr.points]="getArrowheadDimensions().points"
                [attr.fill]="getShapeElement().stroke"
              />
            </marker>
          </defs>
          <line
            [attr.x1]="getLinePointCoordinate('start', 'x')"
            [attr.y1]="getLinePointCoordinate('start', 'y')"
            [attr.x2]="getLinePointCoordinate('end', 'x')"
            [attr.y2]="getLinePointCoordinate('end', 'y')"
            [attr.stroke]="getShapeElement().stroke"
            [attr.stroke-width]="getShapeElement().strokeWidth"
            stroke-linecap="round"
            [attr.marker-end]="'url(#arrowhead-' + element.id + ')'"
          />
        </g>

        <!-- Double Arrow Shape -->
        <g *ngIf="getShapeElement().shapeType === 'double-arrow'">
          <defs>
            <marker
              [attr.id]="'double-arrowhead-start-' + element.id"
              [attr.markerWidth]="getArrowheadDimensions().width"
              [attr.markerHeight]="getArrowheadDimensions().height"
              [attr.refX]="0.5"
              [attr.refY]="getArrowheadDimensions().refY"
              orient="auto"
              markerUnits="strokeWidth"
              overflow="visible"
            >
              <polygon
                [attr.points]="
                  getArrowheadDimensions().width +
                  ' 0, 0 ' +
                  getArrowheadDimensions().refY +
                  ', ' +
                  getArrowheadDimensions().width +
                  ' ' +
                  getArrowheadDimensions().height
                "
                [attr.fill]="getShapeElement().stroke"
              />
            </marker>
            <marker
              [attr.id]="'double-arrowhead-end-' + element.id"
              [attr.markerWidth]="getArrowheadDimensions().width"
              [attr.markerHeight]="getArrowheadDimensions().height"
              [attr.refX]="getArrowheadDimensions().refX"
              [attr.refY]="getArrowheadDimensions().refY"
              orient="auto"
              markerUnits="strokeWidth"
              overflow="visible"
            >
              <polygon
                [attr.points]="getArrowheadDimensions().points"
                [attr.fill]="getShapeElement().stroke"
              />
            </marker>
          </defs>
          <line
            [attr.x1]="getLinePointCoordinate('start', 'x')"
            [attr.y1]="getLinePointCoordinate('start', 'y')"
            [attr.x2]="getLinePointCoordinate('end', 'x')"
            [attr.y2]="getLinePointCoordinate('end', 'y')"
            [attr.stroke]="getShapeElement().stroke"
            [attr.stroke-width]="getShapeElement().strokeWidth"
            stroke-linecap="round"
            [attr.marker-start]="
              'url(#double-arrowhead-start-' + element.id + ')'
            "
            [attr.marker-end]="'url(#double-arrowhead-end-' + element.id + ')'"
          />
        </g>
      </svg>

      <!-- Table Element -->
      <div *ngIf="element.type === 'table'" class="table-wrapper">
        <button
          *ngIf="isSelected"
          class="table-drag-handle"
          title="Drag table"
          type="button"
          (mousedown)="onTableHandleMouseDown($event)"
        >
          <span class="handle-dot"></span>
          <span class="handle-dot"></span>
          <span class="handle-dot"></span>
        </button>

        <div
          class="table-element"
          [style.gridTemplateColumns]="getTableTemplateColumns()"
          [style.gridTemplateRows]="getTableTemplateRows()"
          [style.borderColor]="getTableElement().borderColor || '#1d4ed8'"
          [style.background]="
            getTableElement().backgroundColor || 'rgba(255, 255, 255, 0.9)'
          "
        >
          <ng-container
            *ngFor="let row of getTableElement().cellData; let rowIndex = index"
          >
            <div
              *ngFor="let cell of row; let colIndex = index"
              class="table-cell"
              [class.header-cell]="
                rowIndex < (getTableElement().headerRows || 0)
              "
              [style.padding.px]="getTableElement().cellPadding || 8"
              [style.fontSize.px]="
                getCellStyle(rowIndex, colIndex).fontSize || 14
              "
              [style.fontFamily]="
                getCellStyle(rowIndex, colIndex).fontFamily || 'Inter'
              "
              [style.fontWeight]="
                getCellStyle(rowIndex, colIndex).bold ? '700' : '400'
              "
              [style.fontStyle]="
                getCellStyle(rowIndex, colIndex).italic ? 'italic' : 'normal'
              "
              [style.textDecoration]="
                getCellStyle(rowIndex, colIndex).underline
                  ? 'underline'
                  : 'none'
              "
              [style.textAlign]="
                getCellStyle(rowIndex, colIndex).textAlign || 'left'
              "
              [style.color]="
                getCellStyle(rowIndex, colIndex).color ||
                getTableElement().textColor ||
                '#1f2937'
              "
              contenteditable="true"
              spellcheck="false"
              [attr.data-cell]="rowIndex + '-' + colIndex"
              (mousedown)="onTableCellMouseDown($event, rowIndex, colIndex)"
              (focus)="onTableCellFocus($event, rowIndex, colIndex)"
              (blur)="onTableCellBlur($event, rowIndex, colIndex)"
              (keydown)="onTableCellKeydown($event, rowIndex, colIndex)"
            >
              {{ cell }}
            </div>
          </ng-container>
        </div>
      </div>

      <!-- Resize Handles -->
      <div *ngIf="isSelected && !isLinearShape()" class="resize-handles">
        <div
          class="resize-handle resize-nw"
          (mousedown)="startResize($event, 'nw')"
        ></div>
        <div
          class="resize-handle resize-ne"
          (mousedown)="startResize($event, 'ne')"
        ></div>
        <div
          class="resize-handle resize-sw"
          (mousedown)="startResize($event, 'sw')"
        ></div>
        <div
          class="resize-handle resize-se"
          (mousedown)="startResize($event, 'se')"
        ></div>
        <!-- Optional side handles for quicker single-axis resizing -->
        <div
          class="resize-handle resize-n"
          (mousedown)="startResize($event, 'n')"
        ></div>
        <div
          class="resize-handle resize-s"
          (mousedown)="startResize($event, 's')"
        ></div>
        <div
          class="resize-handle resize-e"
          (mousedown)="startResize($event, 'e')"
        ></div>
        <div
          class="resize-handle resize-w"
          (mousedown)="startResize($event, 'w')"
        ></div>
      </div>

      <!-- Linear Shape (Line/Arrow) 2D Handles -->
      <div *ngIf="isSelected && isLinearShape()" class="line-handles">
        <div
          class="line-handle line-start"
          [class.no-transition]="isLineResizing"
          [style.left.px]="getLinePointCoordinate('start', 'x')"
          [style.top.px]="getLinePointCoordinate('start', 'y')"
          (mousedown)="startLineResize($event, 'start')"
          title="Drag to move start point"
        ></div>
        <div
          class="line-handle line-end"
          [class.no-transition]="isLineResizing"
          [style.left.px]="getLinePointCoordinate('end', 'x')"
          [style.top.px]="getLinePointCoordinate('end', 'y')"
          (mousedown)="startLineResize($event, 'end')"
          title="Drag to move end point"
        ></div>
      </div>

      <!-- Rotation Handle (available for all element types except linear shapes) -->
      <div *ngIf="isSelected && !isLinearShape()" class="rotation-ui">
        <div class="rotation-line" (mousedown)="$event.stopPropagation()"></div>
        <div
          class="rotation-handle"
          title="Drag to rotate (Shift to snap)"
          (mousedown)="startRotate($event)"
        ></div>
        <div *ngIf="isRotating" class="rotation-indicator">
          {{ Math.round(element.rotation || 0) }}°
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .canvas-element {
        position: absolute;
        cursor: move;
        user-select: none;
        border-radius: 4px;
        transition: transform 0.1s ease;
      }

      .canvas-element.dragging {
        cursor: grabbing !important;
      }

      .canvas-element:hover {
        transform: scale(1.02);
      }

      .canvas-element.element-selected {
        outline: 2px solid var(--primary);
        outline-offset: 2px;
      }

      .text-element {
        width: 100%;
        height: 100%;
        padding: 8px;
        outline: none;
        overflow: hidden;
        word-wrap: break-word;
        cursor: text;
        pointer-events: all;
        position: relative;
        z-index: 10;
        border: 2px solid transparent;
        border-radius: 4px;
        transition: all 0.2s ease;
        line-height: 1.4;
        white-space: pre-wrap;
        min-height: 24px;
      }

      .text-element:not(:focus) {
        cursor: move;
      }

      .text-element:focus {
        cursor: text;
        background: rgba(37, 99, 235, 0.05);
        border: 2px dashed var(--primary);
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
      }

      .text-element:hover:not(:focus) {
        background: rgba(37, 99, 235, 0.02);
        border: 2px solid rgba(37, 99, 235, 0.2);
      }

      /* Text style classes for better visual consistency */
      .text-bold {
        font-weight: 700 !important;
      }

      .text-italic {
        font-style: italic !important;
      }

      .text-underline {
        text-decoration: underline !important;
      }

      /* Empty text placeholder */
      .text-element:empty::before {
        content: "Click to edit text";
        color: var(--gray-400);
        font-style: italic;
        pointer-events: none;
      }

      .text-element:focus:empty::before {
        content: "Type your text here...";
        color: var(--gray-500);
      }

      .image-element {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 4px;
        display: block;
        background: var(--gray-50);
      }

      .shape-element {
        width: 100%;
        height: 100%;
      }

      .table-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
      }

      .table-drag-handle {
        position: absolute;
        top: -16px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.65);
        box-shadow: 0 4px 8px rgba(15, 23, 42, 0.25);
        cursor: grab;
        color: #fff;
        font-size: 10px;
        line-height: 1;
        z-index: 20;
        transition: transform 0.15s ease, box-shadow 0.15s ease,
          background 0.15s ease;
      }

      .text-drag-handle {
        position: absolute;
        top: -18px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 999px;
        background: rgba(55, 65, 81, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.65);
        box-shadow: 0 4px 8px rgba(15, 23, 42, 0.25);
        cursor: grab;
        color: #fff;
        font-size: 10px;
        line-height: 1;
        z-index: 20;
        transition: transform 0.15s ease, box-shadow 0.15s ease,
          background 0.15s ease;
      }

      .text-drag-handle:hover {
        background: rgba(31, 41, 55, 0.95);
      }

      .text-drag-handle:active {
        cursor: grabbing;
        transform: translateX(-50%) scale(0.98);
        box-shadow: 0 2px 4px rgba(15, 23, 42, 0.25);
      }

      .table-drag-handle:active {
        cursor: grabbing;
        transform: translateX(-50%) scale(0.98);
        box-shadow: 0 2px 4px rgba(15, 23, 42, 0.25);
      }

      .table-drag-handle:hover {
        background: rgba(29, 78, 216, 0.95);
      }

      .handle-dot {
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.85;
      }

      .table-element {
        width: 100%;
        height: 100%;
        display: grid;
        border: 1px solid #1d4ed8;
        border-radius: 4px;
        overflow: hidden;
        background: rgba(255, 255, 255, 0.9);
        pointer-events: all;
      }

      .table-cell {
        border: 1px solid rgba(29, 78, 216, 0.35);
        display: flex;
        align-items: center;
        justify-content: flex-start;
        font-size: 14px;
        line-height: 1.4;
        background: transparent;
        white-space: pre-wrap;
        word-break: break-word;
        outline: none;
        cursor: text;
        position: relative;
        min-height: 24px;
        box-sizing: border-box;
      }

      .table-cell.header-cell {
        background: rgba(37, 99, 235, 0.12);
        font-weight: 600;
      }

      .table-cell:focus {
        border-color: #1d4ed8;
        box-shadow: inset 0 0 0 1px rgba(29, 78, 216, 0.65);
        background: rgba(37, 99, 235, 0.08);
      }

      .table-cell:empty::before {
        content: "";
        display: inline-block;
      }

      .resize-handles {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1000;
      }

      .resize-handle {
        position: absolute;
        width: 10px;
        height: 10px;
        background: var(--primary);
        border: 2px solid #fff;
        border-radius: 4px;
        pointer-events: all;
        z-index: 1001;
        transition: background 0.15s ease, box-shadow 0.15s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        /* Use transform based centering so outline thickness/zoom doesn't shift placement */
        transform: translate(-50%, -50%);
      }

      .resize-handle:hover {
        background: var(--primary-dark);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9),
          0 2px 6px rgba(37, 99, 235, 0.35);
      }
      .resize-handle:active {
        background: var(--primary-dark);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9),
          0 0 0 4px rgba(37, 99, 235, 0.45);
      }

      /* Larger invisible hit target without changing visual size */
      .resize-handle::after {
        content: "";
        position: absolute;
        left: 50%;
        top: 50%;
        width: 22px;
        height: 22px;
        transform: translate(-50%, -50%);
        /* transparent but keeps pointer capture */
      }

      /* Corner handles - positioned exactly at geometric corners */
      .resize-nw {
        top: 0;
        left: 0;
        cursor: nw-resize;
      }
      .resize-ne {
        top: 0;
        left: 100%;
        cursor: ne-resize;
      }
      .resize-sw {
        top: 100%;
        left: 0;
        cursor: sw-resize;
      }
      .resize-se {
        top: 100%;
        left: 100%;
        cursor: se-resize;
      }

      /* Side handles */
      /* Side handles - centered on edges */
      .resize-n {
        top: 0;
        left: 50%;
        cursor: n-resize;
      }
      .resize-s {
        top: 100%;
        left: 50%;
        cursor: s-resize;
      }
      .resize-e {
        top: 50%;
        left: 100%;
        cursor: e-resize;
      }
      .resize-w {
        top: 50%;
        left: 0;
        cursor: w-resize;
      }

      .resize-size-indicator {
        position: absolute;
        top: -26px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.75);
        color: #fff;
        padding: 2px 6px;
        font-size: 11px;
        border-radius: 4px;
        font-family: monospace;
        pointer-events: none;
        z-index: 1500;
        letter-spacing: 0.5px;
      }

      /* Prevent hover transform when resizing */
      .canvas-element:has(.resize-handle:hover) {
        transform: none;
      }

      /* Better visual feedback for selected elements */
      .canvas-element.element-selected:hover {
        transform: none;
      }

      /* Linear Shape (Line/Arrow) Handles */
      .line-handles {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1000;
      }

      .line-handle {
        position: absolute;
        width: 12px;
        height: 12px;
        background: var(--primary);
        border: 2px solid #fff;
        border-radius: 50%;
        pointer-events: all;
        z-index: 1001;
        transition: all 0.15s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        cursor: move;
        transform: translate(-50%, -50%);
      }

      .line-handle.no-transition {
        transition: none;
      }

      .line-handle:hover {
        background: var(--primary-dark);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9),
          0 2px 6px rgba(37, 99, 235, 0.35);
        transform: translate(-50%, -50%) scale(1.2);
      }

      .line-handle:active {
        background: var(--primary-dark);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9),
          0 0 0 4px rgba(37, 99, 235, 0.45);
      }

      /* Position line handles at start and end points */
      .line-start {
        left: 0;
        bottom: 0;
      }

      .line-end {
        right: 0;
        top: 0;
      }

      /* Rotation UI */
      .rotation-ui {
        position: absolute;
        left: 50%;
        top: 0;
        transform: translateX(-50%);
        pointer-events: none; /* allow only child handle to capture */
        z-index: 1100;
      }

      .rotation-line {
        position: absolute;
        left: 50%;
        top: -28px; /* length of the line */
        width: 2px;
        height: 24px;
        background: var(--primary);
        transform: translateX(-50%);
        pointer-events: none;
      }

      .rotation-handle {
        position: absolute;
        left: 50%;
        top: -36px; /* place knob above the element */
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        border: 2px solid var(--primary);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        transform: translate(-50%, -50%);
        cursor: grab;
        pointer-events: all;
        z-index: 1101;
      }

      .rotation-handle:active {
        cursor: grabbing;
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9),
          0 0 0 4px rgba(37, 99, 235, 0.45);
      }

      .rotation-indicator {
        position: absolute;
        top: -58px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.75);
        color: #fff;
        padding: 2px 6px;
        font-size: 11px;
        border-radius: 4px;
        font-family: monospace;
        pointer-events: none;
        z-index: 1150;
        letter-spacing: 0.5px;
      }
    `,
  ],
})
export class CanvasElementComponent implements OnInit, OnDestroy {
  @Input() element!: AnyElement;
  @Input() isSelected = false;
  @Output() elementSelect = new EventEmitter<AnyElement>();
  @Output() elementUpdate = new EventEmitter<{
    id: string;
    updates: Partial<AnyElement>;
  }>();
  @Output() elementDelete = new EventEmitter<string>();
  @Output() tableCellSelect = new EventEmitter<{
    elementId: string;
    row: number;
    col: number;
  } | null>();

  @ViewChild("elementContainer", { static: true })
  elementContainer!: ElementRef;

  public isDragging = false;
  public isResizing = false; // exposed for template indicator
  private resizeDirection = ""; // corner or side id
  private startMousePos: Position = { x: 0, y: 0 };
  private startElementPos: Position = { x: 0, y: 0 };
  private startElementSize: Size = { width: 0, height: 0 };
  private isTextEditing = false;
  private isTableEditing = false;
  private activeTableCell: { row: number; col: number } | null = null;
  private isLineResizing = false;
  private lineResizeType = ""; // 'start' or 'end'
  private startLinePoints: { start: Position; end: Position } | null = null;
  public isRotating = false;
  private lastMouseAngleDeg = 0; // previous mouse angle during rotation
  private currentRotationDeg = 0; // locally tracked rotation while rotating
  private zoomSubscription?: Subscription;
  private zoomLevel = 1;
  private readonly defaultTableCellStyle: TableCellStyle = {
    fontSize: 14,
    fontFamily: "Inter",
    color: "#1f2937",
    textAlign: "left",
    bold: false,
    italic: false,
    underline: false,
  };
  private boundMouseMove?: (event: MouseEvent) => void;
  private boundMouseUp?: (event: MouseEvent) => void;
  private pendingTextContent: string | null = null;

  public Math = Math;

  public isLinearShape(): boolean {
    return (
      this.element.type === "shape" &&
      (this.getShapeElement().shapeType === "line" ||
        this.getShapeElement().shapeType === "arrow" ||
        this.getShapeElement().shapeType === "double-arrow")
    );
  }

  private getLinePointsWithFallback(): { start: Position; end: Position } {
    if (!this.isLinearShape()) {
      return {
        start: { x: 0, y: this.element.size.height },
        end: { x: this.element.size.width, y: 0 },
      };
    }

    const shape = this.getShapeElement();
    const linePoints = shape.linePoints;

    if (linePoints?.start && linePoints?.end) {
      return {
        start: { ...linePoints.start },
        end: { ...linePoints.end },
      };
    }

    return {
      start: { x: 0, y: this.element.size.height },
      end: { x: this.element.size.width, y: 0 },
    };
  }

  public getLinePointCoordinate(
    point: "start" | "end",
    axis: "x" | "y"
  ): number {
    const points = this.getLinePointsWithFallback();
    return points[point][axis];
  }

  public getArrowheadDimensions(): {
    width: number;
    height: number;
    refX: number;
    refY: number;
    points: string;
  } {
    if (!this.isLinearShape()) {
      return {
        width: 10,
        height: 7,
        refX: 9,
        refY: 3.5,
        points: "0 0, 10 3.5, 0 7",
      };
    }

    const strokeWidth = this.getShapeElement().strokeWidth || 2;
    // Scale arrowhead size based on stroke width, with minimum and maximum bounds
    const baseWidth = Math.max(6, Math.min(14, strokeWidth * 3));
    const baseHeight = Math.max(4, Math.min(10, strokeWidth * 2.5));

    const width = baseWidth;
    const height = baseHeight;
    const refX = width - 0.5; // Slightly inset to prevent clipping
    const refY = height / 2;
    const points = `0 0, ${width} ${refY}, 0 ${height}`;

    return { width, height, refX, refY, points };
  }

  constructor(
    private readonly canvasService: CanvasService,
    private readonly imageUrlService: ImageUrlService
  ) {}

  startLineResize(event: MouseEvent, type: "start" | "end"): void {
    event.preventDefault();
    event.stopPropagation();

    this.isLineResizing = true;
    this.lineResizeType = type;
    this.startMousePos = { x: event.clientX, y: event.clientY };
    this.startElementPos = { ...this.element.position };
    this.startElementSize = { ...this.element.size };

    const currentLinePoints = this.getLinePointsWithFallback();
    this.startLinePoints = {
      start: { ...currentLinePoints.start },
      end: { ...currentLinePoints.end },
    };

    if (!this.getShapeElement().linePoints) {
      this.elementUpdate.emit({
        id: this.element.id,
        updates: {
          linePoints: {
            start: { ...currentLinePoints.start },
            end: { ...currentLinePoints.end },
          },
        },
      });
    }
  }

  ngOnInit(): void {
    const moveHandler = this.onMouseMove.bind(this);
    const upHandler = this.onMouseUp.bind(this);

    this.boundMouseMove = moveHandler;
    this.boundMouseUp = upHandler;

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);

    this.zoomSubscription = this.canvasService.zoom$.subscribe((value) => {
      this.zoomLevel = value;
    });
  }

  ngOnDestroy(): void {
    if (this.boundMouseMove) {
      document.removeEventListener("mousemove", this.boundMouseMove);
    }
    if (this.boundMouseUp) {
      document.removeEventListener("mouseup", this.boundMouseUp);
    }

    this.zoomSubscription?.unsubscribe();
  }

  onElementClick(event: MouseEvent): void {
    event.stopPropagation();
    this.elementSelect.emit(this.element);
  }

  startDrag(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Don't start drag if text is being edited
    if (
      this.isTextEditing ||
      this.isTableEditing ||
      (target.classList.contains("text-element") &&
        document.activeElement === target) ||
      (target.classList.contains("table-cell") &&
        document.activeElement === target)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    this.startMousePos = { x: event.clientX, y: event.clientY };
    this.startElementPos = { ...this.element.position };
    this.elementSelect.emit(this.element);
  }

  startResize(event: MouseEvent, direction: string): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeDirection = direction;
    this.startMousePos = { x: event.clientX, y: event.clientY };
    this.startElementPos = { ...this.element.position };
    this.startElementSize = { ...this.element.size };
  }

  startRotate(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isRotating = true;

    // Calculate element center in viewport coordinates
    const el: HTMLElement = this.elementContainer.nativeElement;
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseAngle = Math.atan2(
      event.clientY - centerY,
      event.clientX - centerX
    );
    this.lastMouseAngleDeg = (mouseAngle * 180) / Math.PI;
    this.currentRotationDeg = this.element.rotation || 0;
  }

  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const zoom = this.zoomLevel || 1;
      const deltaX = (event.clientX - this.startMousePos.x) / zoom;
      const deltaY = (event.clientY - this.startMousePos.y) / zoom;

      // Get the canvas container bounds for proper boundary checking
      const canvasElement =
        this.elementContainer.nativeElement.closest(".canvas-area");
      const canvasBounds = canvasElement?.getBoundingClientRect();
      const boundsWidth = canvasBounds
        ? canvasBounds.width / zoom
        : this.canvasService.canvasDimensions.width;
      const boundsHeight = canvasBounds
        ? canvasBounds.height / zoom
        : this.canvasService.canvasDimensions.height;

      let newX = this.startElementPos.x + deltaX;
      let newY = this.startElementPos.y + deltaY;

      // Ensure element stays within canvas bounds
      if (canvasBounds) {
        newX = Math.max(
          0,
          Math.min(newX, boundsWidth - this.element.size.width)
        );
        newY = Math.max(
          0,
          Math.min(newY, boundsHeight - this.element.size.height)
        );
      } else {
        // Fallback boundary checking
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);
      }
      const newPosition: Position = {
        x: newX,
        y: newY,
      };

      this.elementUpdate.emit({
        id: this.element.id,
        updates: { position: newPosition },
      });
    }

    if (this.isResizing) {
      this.handleResize(event);
    }

    if (this.isLineResizing) {
      this.handleLineResize(event);
    }

    if (this.isRotating) {
      this.handleRotate(event);
    }
  }

  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
    this.isLineResizing = false;
    this.isRotating = false;
    this.resizeDirection = "";
    this.lineResizeType = "";
    this.startLinePoints = null;
  }

  onTextClick(event: MouseEvent): void {
    event.stopPropagation();
    this.elementSelect.emit(this.element);

    // Single click to start editing if not already editing
    if (!this.isTextEditing) {
      this.isTextEditing = true;
      this.pendingTextContent = this.getTextElement().content;
      setTimeout(() => {
        const target = event.target as HTMLDivElement;
        target.focus();
        // Select all text if it's the default placeholder
        if (target.textContent === "Your text here") {
          this.selectAllText(target);
        }
      }, 0);
    }
  }

  onTextKeydown(event: KeyboardEvent): void {
    // Handle keyboard shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "b":
          event.preventDefault();
          this.updateTextStyle("bold");
          break;
        case "i":
          event.preventDefault();
          this.updateTextStyle("italic");
          break;
        case "u":
          event.preventDefault();
          this.updateTextStyle("underline");
          break;
      }
    }

    // Handle Enter key
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const target = event.target as HTMLDivElement;
      target.blur();
    }

    // Handle Escape key
    if (event.key === "Escape") {
      event.preventDefault();
      const target = event.target as HTMLDivElement;
      target.blur();
    }
  }

  private updateTextStyle(style: "bold" | "italic" | "underline"): void {
    if (this.element.type !== "text") return;

    const textElement = this.getTextElement();
    const currentValue = textElement[style] || false;

    const updates: any = {
      [style]: !currentValue,
    };

    // If toggling bold, also update fontWeight for consistency
    if (style === "bold") {
      updates.fontWeight = !currentValue ? "700" : "400";
    }

    this.elementUpdate.emit({
      id: this.element.id,
      updates,
    });
  }

  onTextInput(event: Event): void {
    const target = event.target as HTMLDivElement;
    this.pendingTextContent = target.textContent || "";
  }

  updateTextContent(event: Event): void {
    const target = event.target as HTMLDivElement;
    const content = this.pendingTextContent ?? target.textContent ?? "";

    this.isTextEditing = false;
    this.pendingTextContent = null;

    if (content !== this.getTextElement().content) {
      // Final content update on blur
      this.elementUpdate.emit({
        id: this.element.id,
        updates: { content },
      });
    }
  }

  private selectAllText(element: HTMLDivElement): void {
    const range = document.createRange();
    range.selectNodeContents(element);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  public getTextElement(): TextElement {
    return this.element.type === "text"
      ? (this.element as TextElement)
      : ({} as TextElement);
  }

  public getImageElement(): ImageElement {
    return this.element.type === "image"
      ? (this.element as ImageElement)
      : ({} as ImageElement);
  }

  public getResolvedImageUrl(): string {
    if (this.element.type !== "image") return "";
    const imageElement = this.getImageElement();
    return this.imageUrlService.resolveImageUrl(imageElement.url);
  }

  public getShapeElement(): ShapeElement {
    return this.element.type === "shape"
      ? (this.element as ShapeElement)
      : ({} as ShapeElement);
  }

  public getTableElement(): TableElement {
    return this.element.type === "table"
      ? (this.element as TableElement)
      : ({} as TableElement);
  }

  public getTableTemplateColumns(): string {
    const table = this.getTableElement();
    if (!table || !table.columns) {
      return "1fr";
    }
    return `repeat(${table.columns}, 1fr)`;
  }

  public getTableTemplateRows(): string {
    const table = this.getTableElement();
    if (!table || !table.rows) {
      return "1fr";
    }
    return `repeat(${table.rows}, 1fr)`;
  }

  public getCellStyle(rowIndex: number, colIndex: number): TableCellStyle {
    if (this.element.type !== "table") {
      return this.defaultTableCellStyle;
    }

    const table = this.getTableElement();
    const style = table.cellStyles?.[rowIndex]?.[colIndex];

    if (!style) {
      return this.defaultTableCellStyle;
    }

    return style;
  }

  onTableCellMouseDown(
    event: MouseEvent,
    rowIndex: number,
    colIndex: number
  ): void {
    event.stopPropagation();
    this.isTableEditing = true;
    this.activeTableCell = { row: rowIndex, col: colIndex };
    this.tableCellSelect.emit({
      elementId: this.element.id,
      row: rowIndex,
      col: colIndex,
    });
    this.elementSelect.emit(this.element);
  }

  onTableHandleMouseDown(event: MouseEvent): void {
    this.isTableEditing = false;
    this.activeTableCell = null;
    this.tableCellSelect.emit(null);
    this.startDrag(event);
  }

  onTextHandleMouseDown(event: MouseEvent): void {
    const textElement = this.elementContainer.nativeElement.querySelector(
      ".text-element"
    ) as HTMLDivElement | null;

    if (textElement && document.activeElement === textElement) {
      textElement.blur();
    }

    this.isTextEditing = false;
    this.pendingTextContent = null;
    this.startDrag(event);
  }

  onTableCellFocus(
    event: FocusEvent,
    rowIndex: number,
    colIndex: number
  ): void {
    this.isTableEditing = true;
    this.activeTableCell = { row: rowIndex, col: colIndex };
    this.tableCellSelect.emit({
      elementId: this.element.id,
      row: rowIndex,
      col: colIndex,
    });
    this.elementSelect.emit(this.element);
    const target = event.target as HTMLDivElement;
    this.placeCaretAtEnd(target);
  }

  onTableCellBlur(event: FocusEvent, rowIndex: number, colIndex: number): void {
    const target = event.target as HTMLDivElement;
    const value = target.textContent || "";
    const currentValue =
      this.getTableElement().cellData[rowIndex]?.[colIndex] || "";

    this.isTableEditing = false;

    if (value !== currentValue) {
      this.updateTableCell(rowIndex, colIndex, value);
    }
  }

  onTableCellKeydown(
    event: KeyboardEvent,
    rowIndex: number,
    colIndex: number
  ): void {
    const table = this.getTableElement();

    if (event.key === "Escape") {
      event.preventDefault();
      (event.target as HTMLElement).blur();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      const totalCells = table.rows * table.columns;
      if (!totalCells) {
        return;
      }

      const currentIndex = rowIndex * table.columns + colIndex;
      let nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
      nextIndex = Math.max(0, Math.min(totalCells - 1, nextIndex));

      const targetRow = Math.floor(nextIndex / table.columns);
      const targetCol = nextIndex % table.columns;
      this.focusTableCell(targetRow, targetCol);
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const targetRow = Math.min(table.rows - 1, rowIndex + 1);
      this.focusTableCell(targetRow, colIndex);
    }
  }

  private updateTableCell(
    rowIndex: number,
    colIndex: number,
    value: string
  ): void {
    if (this.element.type !== "table") return;

    const table = this.getTableElement();
    if (!table.cellData[rowIndex]) return;

    const newData = table.cellData.map((row, rIdx) =>
      row.map((cell, cIdx) =>
        rIdx === rowIndex && cIdx === colIndex ? value : cell
      )
    );

    this.elementUpdate.emit({
      id: this.element.id,
      updates: { cellData: newData },
    });
  }

  private focusTableCell(rowIndex: number, colIndex: number): void {
    const table = this.getTableElement();
    if (
      rowIndex < 0 ||
      rowIndex >= table.rows ||
      colIndex < 0 ||
      colIndex >= table.columns
    ) {
      return;
    }

    this.activeTableCell = { row: rowIndex, col: colIndex };
    this.tableCellSelect.emit({
      elementId: this.element.id,
      row: rowIndex,
      col: colIndex,
    });

    const selector = `[data-cell="${rowIndex}-${colIndex}"]`;
    const cell = this.elementContainer.nativeElement.querySelector(
      selector
    ) as HTMLDivElement | null;

    if (cell) {
      requestAnimationFrame(() => {
        cell.focus();
        this.placeCaretAtEnd(cell);
      });
    }
  }

  private placeCaretAtEnd(element: HTMLElement): void {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  public getTrianglePoints(): string {
    const w = this.element.size.width;
    const h = this.element.size.height;
    return `${w / 2},${this.getShapeElement().strokeWidth} ${
      w - this.getShapeElement().strokeWidth
    },${h - this.getShapeElement().strokeWidth} ${
      this.getShapeElement().strokeWidth
    },${h - this.getShapeElement().strokeWidth}`;
  }

  public getDiamondPoints(): string {
    const w = this.element.size.width;
    const h = this.element.size.height;
    const sw = this.getShapeElement().strokeWidth;
    return `${w / 2},${sw} ${w - sw},${h / 2} ${w / 2},${h - sw} ${sw},${
      h / 2
    }`;
  }

  public getStarPoints(): string {
    const w = this.element.size.width;
    const h = this.element.size.height;
    const cx = w / 2;
    const cy = h / 2;
    const outerRadius = Math.min(w, h) / 2 - this.getShapeElement().strokeWidth;
    const innerRadius = outerRadius * 0.4;

    let points = "";
    for (let i = 0; i < 10; i++) {
      const angle = (i * Math.PI) / 5 - Math.PI / 2;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      points += `${x},${y} `;
    }
    return points.trim();
  }

  public getHexagonPoints(): string {
    const w = this.element.size.width;
    const h = this.element.size.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) / 2 - this.getShapeElement().strokeWidth;

    let points = "";
    for (let i = 0; i < 6; i++) {
      const angle = (i * Math.PI) / 3;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      points += `${x},${y} `;
    }
    return points.trim();
  }
  updateTextProperty(property: string, event: any): void {
    if (this.element.type !== "text") return;

    const value = event.target ? event.target.value : event;
    this.elementUpdate.emit({
      id: this.element.id,
      updates: {
        [property]: property === "fontSize" ? parseInt(value) : value,
      },
    });
  }

  private rafPending = false;
  private lastResizeEvent: MouseEvent | null = null;
  private lineRafPending = false;
  private lastLineResizeEvent: MouseEvent | null = null;

  private handleResize(event: MouseEvent): void {
    this.lastResizeEvent = event;
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      if (!this.lastResizeEvent) return;
      const e = this.lastResizeEvent;
      const zoom = this.zoomLevel || 1;
      const deltaX = (e.clientX - this.startMousePos.x) / zoom;
      const deltaY = (e.clientY - this.startMousePos.y) / zoom;

      const keepRatio = e.shiftKey; // preserve aspect ratio on Shift
      const aspect =
        this.startElementSize.width / this.startElementSize.height || 1;

      let newSize = { ...this.startElementSize };
      let newPosition = { ...this.startElementPos };

      const applyMin = () => {
        newSize.width = Math.max(20, newSize.width);
        newSize.height = Math.max(20, newSize.height);
      };

      switch (this.resizeDirection) {
        case "se":
          newSize.width = this.startElementSize.width + deltaX;
          newSize.height = this.startElementSize.height + deltaY;
          if (keepRatio) newSize.height = newSize.width / aspect;
          break;
        case "sw":
          newSize.width = this.startElementSize.width - deltaX;
          if (keepRatio) newSize.height = newSize.width / aspect;
          else newSize.height = this.startElementSize.height + deltaY;
          newPosition.x = this.startElementPos.x + deltaX;
          break;
        case "ne":
          newSize.width = this.startElementSize.width + deltaX;
          if (keepRatio) newSize.height = newSize.width / aspect;
          else newSize.height = this.startElementSize.height - deltaY;
          newPosition.y = this.startElementPos.y + deltaY;
          break;
        case "nw":
          newSize.width = this.startElementSize.width - deltaX;
          if (keepRatio) newSize.height = newSize.width / aspect;
          else newSize.height = this.startElementSize.height - deltaY;
          newPosition.x = this.startElementPos.x + deltaX;
          newPosition.y = this.startElementPos.y + deltaY;
          break;
        case "n":
          newSize.height = this.startElementSize.height - deltaY;
          if (keepRatio) newSize.width = newSize.height * aspect;
          newPosition.y = this.startElementPos.y + deltaY;
          break;
        case "s":
          newSize.height = this.startElementSize.height + deltaY;
          if (keepRatio) newSize.width = newSize.height * aspect;
          break;
        case "e":
          newSize.width = this.startElementSize.width + deltaX;
          if (keepRatio) newSize.height = newSize.width / aspect;
          break;
        case "w":
          newSize.width = this.startElementSize.width - deltaX;
          if (keepRatio) newSize.height = newSize.width / aspect;
          newPosition.x = this.startElementPos.x + deltaX;
          break;
      }

      applyMin();

      // Constrain into canvas bounds if possible
      const canvasElement =
        this.elementContainer.nativeElement.closest(".canvas-area");
      if (canvasElement) {
        // Bounds available if needed: const bounds = canvasElement.getBoundingClientRect();
      }

      this.elementUpdate.emit({
        id: this.element.id,
        updates: { size: newSize, position: newPosition },
      });
    });
  }

  private handleLineResize(event: MouseEvent): void {
    this.lastLineResizeEvent = event;
    if (this.lineRafPending) return;
    this.lineRafPending = true;
    requestAnimationFrame(() => {
      this.lineRafPending = false;
      if (!this.lastLineResizeEvent) return;
      const e = this.lastLineResizeEvent;

      const zoom = this.zoomLevel || 1;
      const deltaX = (e.clientX - this.startMousePos.x) / zoom;
      const deltaY = (e.clientY - this.startMousePos.y) / zoom;
      const basePoints = this.startLinePoints
        ? {
            start: { ...this.startLinePoints.start },
            end: { ...this.startLinePoints.end },
          }
        : this.getLinePointsWithFallback();

      const startAbsolute = {
        x: this.startElementPos.x + basePoints.start.x,
        y: this.startElementPos.y + basePoints.start.y,
      };
      const endAbsolute = {
        x: this.startElementPos.x + basePoints.end.x,
        y: this.startElementPos.y + basePoints.end.y,
      };

      if (this.lineResizeType === "start") {
        startAbsolute.x += deltaX;
        startAbsolute.y += deltaY;
      } else if (this.lineResizeType === "end") {
        endAbsolute.x += deltaX;
        endAbsolute.y += deltaY;
      }

      if (e.shiftKey) {
        const anchor =
          this.lineResizeType === "start" ? endAbsolute : startAbsolute;
        const moving =
          this.lineResizeType === "start" ? startAbsolute : endAbsolute;
        const dx = moving.x - anchor.x;
        const dy = moving.y - anchor.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > 0) {
          const snapAngle =
            Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
          moving.x = anchor.x + distance * Math.cos(snapAngle);
          moving.y = anchor.y + distance * Math.sin(snapAngle);
          if (this.lineResizeType === "start") {
            startAbsolute.x = moving.x;
            startAbsolute.y = moving.y;
          } else {
            endAbsolute.x = moving.x;
            endAbsolute.y = moving.y;
          }
        }
      }

      let minX = Math.min(startAbsolute.x, endAbsolute.x);
      let minY = Math.min(startAbsolute.y, endAbsolute.y);
      let maxX = Math.max(startAbsolute.x, endAbsolute.x);
      let maxY = Math.max(startAbsolute.y, endAbsolute.y);

      const MIN_DIMENSION = 8;

      if (maxX - minX < MIN_DIMENSION) {
        const expand = (MIN_DIMENSION - (maxX - minX)) / 2;
        minX -= expand;
        maxX += expand;
      }

      if (maxY - minY < MIN_DIMENSION) {
        const expand = (MIN_DIMENSION - (maxY - minY)) / 2;
        minY -= expand;
        maxY += expand;
      }

      const newPosition = { x: minX, y: minY };
      const newSize = {
        width: maxX - minX,
        height: maxY - minY,
      };

      const updatedLinePoints = {
        start: {
          x: startAbsolute.x - newPosition.x,
          y: startAbsolute.y - newPosition.y,
        },
        end: {
          x: endAbsolute.x - newPosition.x,
          y: endAbsolute.y - newPosition.y,
        },
      };

      this.elementUpdate.emit({
        id: this.element.id,
        updates: {
          position: newPosition,
          size: newSize,
          linePoints: updatedLinePoints,
        },
      });
    });
  }

  private handleRotate(event: MouseEvent): void {
    // Compute current angle from element center to mouse
    const el: HTMLElement = this.elementContainer.nativeElement;
    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseAngle = Math.atan2(
      event.clientY - centerY,
      event.clientX - centerX
    );
    const mouseAngleDeg = (mouseAngle * 180) / Math.PI;

    // Delta from last frame, adjusted for wrap
    let delta = mouseAngleDeg - this.lastMouseAngleDeg;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;

    let newAngle = this.currentRotationDeg + delta;

    // Snap to 15° increments when holding Shift
    if (event.shiftKey) {
      newAngle = Math.round(newAngle / 15) * 15;
    }

    // Update local trackers for smooth continuous rotation
    this.lastMouseAngleDeg = mouseAngleDeg;
    this.currentRotationDeg = newAngle;

    this.elementUpdate.emit({
      id: this.element.id,
      updates: { rotation: newAngle },
    });
  }

  private normalizeAngle(angle: number): number {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  }
}
